<template>
    <div class="page-container">
        <div class="flex items-center justify-between mb-8">
            <div class="flex items-center gap-4">
                <div class="bg-blue-600 p-3 rounded-2xl shadow-lg">
                    <LucideIcon :size="24" class="text-white">
                        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                    </LucideIcon>
                </div>
                <div>
                    <h2 class="page-title text-2xl font-bold text-slate-900">{{ $t('household.tempStorage.title') }}</h2>
                    <p class="section-subtitle text-slate-500">{{ $t('household.tempStorage.subtitle') }}</p>
                </div>
            </div>
            <button
                @click="openAddDialog"
                class="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all shadow-md active:scale-95"
            >
                <LucideIcon :size="18">
                    <path d="M5 12h14"/><path d="M12 5v14"/>
                </LucideIcon>
                {{ $t('household.tempStorage.acceptBtn') }}
            </button>
        </div>

        <!-- Статистика -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div class="stat-card">
                <div class="flex items-center justify-between">
                    <div>
                        <p class="text-sm font-bold text-slate-400 mb-2 uppercase tracking-tight">{{ $t('household.tempStorage.statActive') }}</p>
                        <p class="text-3xl font-extrabold text-blue-600">{{ activeStorage.length }}</p>
                    </div>
                    <div class="bg-blue-50 p-3 rounded-xl">
                        <LucideIcon :size="24" class="text-blue-600">
                            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                        </LucideIcon>
                    </div>
                </div>
            </div>

            <div class="stat-card">
                <div class="flex items-center justify-between">
                    <div>
                        <p class="text-sm font-bold text-slate-400 mb-2 uppercase tracking-tight">{{ $t('household.tempStorage.statReturned') }}</p>
                        <p class="text-3xl font-extrabold text-green-600">{{ returnedStorage.length }}</p>
                    </div>
                    <div class="bg-green-50 p-3 rounded-xl">
                        <LucideIcon :size="24" class="text-green-600">
                             <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                        </LucideIcon>
                    </div>
                </div>
            </div>

            <div class="stat-card">
                <div class="flex items-center justify-between">
                    <div>
                        <p class="text-sm font-bold text-slate-400 mb-2 uppercase tracking-tight">{{ $t('household.tempStorage.statExpired') }}</p>
                        <p class="text-3xl font-extrabold text-red-600">{{ expiredStorage.length }}</p>
                    </div>
                    <div class="bg-red-50 p-3 rounded-xl">
                        <LucideIcon :size="24" class="text-red-600">
                            <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                        </LucideIcon>
                    </div>
                </div>
            </div>
        </div>

        <!-- Списки -->
        <div v-if="loading" class="flex justify-center py-12">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
        <div v-else class="space-y-6">
            <StorageList
                v-if="activeStorage.length > 0"
                :items="activeStorage"
                :title="$t('household.tempStorage.sectionActive')"
                bg-color="bg-blue-600"
                @return="handleReturn"
                :nomenclatures="nomenclatures"
                :warehouses="warehouses"
                :counterparties="counterparties"
            />

            <StorageList
                v-if="returnedStorage.length > 0"
                :items="returnedStorage"
                :title="$t('household.tempStorage.sectionReturned')"
                bg-color="bg-green-600"
                :nomenclatures="nomenclatures"
                :warehouses="warehouses"
                :counterparties="counterparties"
            />

            <StorageList
                v-if="expiredStorage.length > 0"
                :items="expiredStorage"
                :title="$t('household.tempStorage.sectionExpired')"
                bg-color="bg-red-600"
                :nomenclatures="nomenclatures"
                :warehouses="warehouses"
                :counterparties="counterparties"
            />

            <div v-if="tempStorage.length === 0" class="empty-state-card mt-8">
                <LucideIcon :size="48" class="empty-icon">
                    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </LucideIcon>
                <p>{{ $t('household.tempStorage.empty') }}</p>
                <button class="link-btn" @click="isDialogOpen = true">{{ $t('household.tempStorage.emptyAddBtn') }}</button>
            </div>
        </div>

        <!-- Диалог -->
        <div v-if="isDialogOpen" class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div class="bg-white rounded-3xl p-8 w-full max-w-2xl shadow-2xl scale-in">
                <div class="flex items-center gap-4 mb-8">
                    <div class="bg-blue-600 p-2.5 rounded-xl shadow-lg shadow-blue-100">
                        <LucideIcon :size="20" class="text-white"><path d="M5 12h14"/><path d="M12 5v14"/></LucideIcon>
                    </div>
                    <h3 class="text-xl font-black text-slate-900 leading-tight">
                        {{ $t('household.tempStorage.dialogTitle') }}
                    </h3>
                </div>

                <form @submit.prevent="handleSubmit" class="space-y-6">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">{{ $t('household.tempStorage.nomenclatureLabel') }}</label>
                            <select required v-model="formData.nomenclatureId" class="form-input w-full">
                                <option value="">{{ $t('household.tempStorage.chooseNomenclature') }}</option>
                                <option v-for="n in nomenclatures" :key="n.id" :value="n.id">
                                    {{ n.name }}
                                </option>
                            </select>
                        </div>

                        <div>
                            <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">{{ $t('household.tempStorage.quantityLabel') }}</label>
                            <input type="number" required min="0" v-model.number="formData.quantity" class="form-input w-full" placeholder="0" />
                        </div>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">{{ $t('household.tempStorage.counterpartyLabel') }}</label>
                            <select required v-model="formData.counterpartyId" class="form-input w-full">
                                <option value="">{{ $t('household.tempStorage.chooseCounterparty') }}</option>
                                <option v-for="c in counterparties" :key="c.id" :value="c.id">
                                    {{ c.name }}
                                </option>
                            </select>
                        </div>

                        <div>
                            <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">{{ $t('household.tempStorage.warehouseLabel') }}</label>
                            <select required v-model="formData.warehouseId" class="form-input w-full">
                                <option value="">{{ $t('household.tempStorage.chooseWarehouse') }}</option>
                                <option v-for="w in warehouses" :key="w.id" :value="w.id">
                                    {{ w.name }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">{{ $t('household.tempStorage.startDateLabel') }}</label>
                            <DatePicker v-model="formData.startDate" format="iso" class="form-input w-full" />
                        </div>

                        <div>
                            <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">{{ $t('household.tempStorage.endDateLabel') }}</label>
                            <DatePicker v-model="formData.endDate" format="iso" class="form-input w-full" />
                        </div>
                    </div>

                    <div>
                        <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">{{ $t('household.tempStorage.storageAddressLabel') }}</label>
                        <div class="grid grid-cols-4 gap-3">
                            <input type="text" required v-model="formData.zone" :placeholder="$t('household.tempStorage.zonePh')" class="form-input text-center" />
                            <input type="text" required v-model="formData.row" :placeholder="$t('household.tempStorage.rowPh')" class="form-input text-center" />
                            <input type="text" required v-model="formData.rack" :placeholder="$t('household.tempStorage.rackPh')" class="form-input text-center" />
                            <input type="text" required v-model="formData.level" :placeholder="$t('household.tempStorage.levelPh')" class="form-input text-center" />
                        </div>
                    </div>

                    <div>
                        <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">{{ $t('household.tempStorage.notesLabel') }}</label>
                        <textarea v-model="formData.notes" rows="2" :placeholder="$t('household.tempStorage.notesPlaceholder')" class="form-input w-full"></textarea>
                    </div>

                    <div class="flex gap-4 pt-4">
                        <button type="submit" class="btn-primary-modal flex-1 active:scale-95">{{ $t('household.tempStorage.submitBtn') }}</button>
                        <button type="button" @click="closeDialog" class="btn-secondary flex-1 active:scale-95">{{ $t('common.cancel') }}</button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LucideIcon from '@/components/common/LucideIcon.vue';
import { getTempStorage, createTempStorage, updateTempStorage } from '@/api/household_special';
import { getNomenclatures } from '@/api/household_nomenclatures';
import { getWarehouses } from '@/api/household_warehouses';
import { getCounterparties } from '@/api/household_counterparties';
import StorageList from './components/StorageList.vue';
import DatePicker from '@/components/widgets/DatePicker.vue';

const { t } = useI18n();
const loading = ref(true);
const tempStorage = ref([]);
const nomenclatures = ref([]);
const warehouses = ref([]);
const counterparties = ref([]);
const isDialogOpen = ref(false);

const formData = ref({
    nomenclatureId: '',
    quantity: 0,
    counterpartyId: '',
    warehouseId: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    zone: '',
    row: '',
    rack: '',
    level: '',
    notes: '',
});

const activeStorage = computed(() => tempStorage.value.filter(s => s.status === 'active'));
const returnedStorage = computed(() => tempStorage.value.filter(s => s.status === 'returned'));
const expiredStorage = computed(() => tempStorage.value.filter(s => s.status === 'expired'));

const fetchData = async () => {
    loading.value = true;
    try {
        const [storageRes, nomRes, whRes, cpRes] = await Promise.all([
            getTempStorage(),
            getNomenclatures(),
            getWarehouses(),
            getCounterparties()
        ]);
        tempStorage.value = Array.isArray(storageRes.data) ? storageRes.data : (storageRes.data?.results || []);
        nomenclatures.value = Array.isArray(nomRes.data) ? nomRes.data : (nomRes.data?.results || []);
        warehouses.value = Array.isArray(whRes.data) ? whRes.data : (whRes.data?.results || []);
        counterparties.value = Array.isArray(cpRes.data) ? cpRes.data : (cpRes.data?.results || []);
    } catch (error) {
        console.error('Error fetching temp storage data:', error);
    } finally {
        loading.value = false;
    }
};

const openAddDialog = () => {
    formData.value = {
        nomenclatureId: '',
        quantity: 0,
        counterpartyId: '',
        warehouseId: '',
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        zone: '',
        row: '',
        rack: '',
        level: '',
        notes: '',
    };
    isDialogOpen.value = true;
};

const closeDialog = () => {
    isDialogOpen.value = false;
};

const handleSubmit = async () => {
    try {
        const payload = {
            ...formData.value,
            location: {
                zone: formData.value.zone,
                row: formData.value.row,
                rack: formData.value.rack,
                level: formData.value.level,
            },
            status: 'active'
        };
        await createTempStorage(payload);
        // Force refresh for demo
        fetchData();
        closeDialog();
    } catch (error) {
        console.error('Error accepting for storage:', error);
    }
};

const handleReturn = async (id) => {
    if (confirm(t('household.tempStorage.confirmReturn'))) {
        try {
            await updateTempStorage(id, { status: 'returned' });
            fetchData();
        } catch (error) {
            console.error('Error returning item:', error);
        }
    }
};

onMounted(fetchData);
</script>

<style scoped>
.page-container { padding: 2rem; background: #f3f3f5; min-height: 100%; }
.page-title { color: #000000; font-size: 1.75rem; font-weight: 700; margin: 0; }
.section-subtitle { color: #64748b; font-size: 0.875rem; margin-top: 0.25rem; }

.stat-card {
    background: white;
    padding: 1.5rem 2rem;
    border-radius: 1.25rem;
    border: 1px solid #eef0f4;
    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -1px rgba(0,0,0,0.01);
}

.form-input {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 1rem;
    padding: 0.75rem 1rem;
    color: #1e293b;
    font-size: 0.875rem;
    font-weight: 600;
    transition: all 0.2s;
}

.form-input:focus {
    border-color: #3b82f6;
    background: white;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
    outline: none;
}

.scale-in { animation: scaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
@keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }

.empty-state-card { text-align: center; padding: 5rem 2rem; background: white; border: 1px dashed #e2e8f0; border-radius: 1.25rem; }
.empty-icon { color: #cbd5e1; margin-bottom: 1.5rem; }
.empty-state-card p { color: #64748b; font-size: 1.1rem; margin-bottom: 1.5rem; }
.link-btn { background: #eff6ff; border: none; color: #2563eb; padding: 0.5rem 1rem; border-radius: 0.5rem; font-weight: 600; cursor: pointer; transition: all 0.2s; }
.link-btn:hover { background: #dbeafe; }
</style>
